import React, { Component } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { combineEventFilters } from "@formant/core/common/combineFilters";
import { distinct } from "@formant/core/common/distinct";
import { emptyArrayToUndefined } from "@formant/core/common/emptyArrayToUndefined";
import { toLegacyTagsets } from "@formant/core/common/toLegacyTagsets";
import { EventType } from "@formant/core/model/EventType";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { IFilter } from "@formant/core/model/IFilter";
import { ITagSets } from "@formant/core/model/ITagSets";
import { Severity } from "@formant/core/model/Severity";
import { StreamType } from "@formant/core/model/StreamType";
import { Uuid } from "@formant/core/model/Uuid";

import { eventStore, tagTemplateStore } from "../../stores";
import { StreamNameSelect } from "../common/StreamNameSelect";
import { StreamTypeSelect } from "../common/StreamTypeSelect";
import { AnnotationTemplateSelect } from "./AnnotationTemplateSelect";
import { DeviceSelect } from "./DeviceSelect";
import * as styles from "./EditEventFilter.scss";
import { EventTypeSelect } from "./EventTypeSelect";
import { SeveritySelect } from "./SeveritySelect";
import { TagSelect } from "./TagSelect";
import { UserSelect } from "./UserSelect";

export interface IEditEventFilterProps {
    filter: IEventFilter;
    context: IFilter;
    hideDeviceFilter?: boolean;
    className?: string;
    onChange: (value: IEventFilter) => void;
}

@observer
export class EditEventFilter extends Component<IEditEventFilterProps> {
    private tags: ITagSets = {};
    public render() {
        const {
            onChangeEventTypes,
            onChangeUserIds,
            onChangeAnnotationTemplateIds,
            onChangeDeviceIds,
            onChangeStreamNames,
            onChangeStreamTypes,
            onChangeTags,
            onChangeSeverities,
        } = this;
        const { filter, context, hideDeviceFilter, className } = this.props;
        const {
            eventTypes,
            userIds,
            annotationTemplateIds,
            deviceIds,
            names,
            types,
            severities,
        } = filter;

        this.tags =
            eventStore.tags(
                combineEventFilters(context, { ...filter, tags: undefined }),
            ) || this.tags;
        const tags = this.tags;
        const tagKeys = distinct([
            ...(tagTemplateStore.list("event")?.map(_ => _.tagKey) || []),
            ...Object.keys(filter.tags || {}),
        ]).sort();

        return (
            <div className={classnames(styles["edit-event-filter"], className)}>
                <EventTypeSelect
                    value={eventTypes || []}
                    filter={filter}
                    context={context}
                    onChange={onChangeEventTypes}
                    className={styles["event-type-select"]}
                />
                <SeveritySelect
                    value={severities || []}
                    filter={filter}
                    context={context}
                    onChange={onChangeSeverities}
                    className={styles["event-type-select"]}
                />
                <UserSelect
                    value={userIds || []}
                    filter={filter}
                    context={context}
                    onChange={onChangeUserIds}
                    className={styles["event-type-select"]}
                />
                <AnnotationTemplateSelect
                    value={annotationTemplateIds || []}
                    filter={filter}
                    context={context}
                    onChange={onChangeAnnotationTemplateIds}
                    className={styles["event-type-select"]}
                />
                {!hideDeviceFilter && (
                    <DeviceSelect
                        value={deviceIds || []}
                        filter={filter}
                        context={context}
                        onChange={onChangeDeviceIds}
                        className={styles["event-type-select"]}
                    />
                )}
                <StreamNameSelect
                    streamFilter="event"
                    value={names || []}
                    onChange={onChangeStreamNames}
                    className={styles["event-type-select"]}
                />
                <StreamTypeSelect
                    streamFilter="event"
                    value={types || []}
                    onChange={onChangeStreamTypes}
                    className={styles["event-type-select"]}
                />
                {tagKeys.map(key => (
                    <TagSelect
                        key={key}
                        tagKey={key}
                        value={toLegacyTagsets(filter.tags)?.[key] || []}
                        options={tags[key] || []}
                        onChange={onChangeTags}
                        className={styles["event-type-select"]}
                    />
                ))}
            </div>
        );
    }

    private onChangeEventTypes = (eventTypes: EventType[]) => {
        const { filter, onChange } = this.props;
        onChange({
            ...filter,
            eventTypes: emptyArrayToUndefined(eventTypes),
        });
    };

    private onChangeUserIds = (userIds: Uuid[]) => {
        const { filter, onChange } = this.props;
        onChange({
            ...filter,
            userIds: emptyArrayToUndefined(userIds),
        });
    };

    private onChangeAnnotationTemplateIds = (annotationTemplateIds: Uuid[]) => {
        const { filter, onChange } = this.props;
        onChange({
            ...filter,
            annotationTemplateIds: emptyArrayToUndefined(annotationTemplateIds),
        });
    };

    private onChangeDeviceIds = (deviceIds: Uuid[]) => {
        const { filter, onChange } = this.props;
        onChange({
            ...filter,
            deviceIds: emptyArrayToUndefined(deviceIds),
        });
    };

    private onChangeStreamNames = (names: string[]) => {
        const { filter, onChange } = this.props;
        onChange({
            ...filter,
            names: emptyArrayToUndefined(names),
        });
    };

    private onChangeStreamTypes = (types: StreamType[]) => {
        const { filter, onChange } = this.props;
        onChange({
            ...filter,
            types: emptyArrayToUndefined(types),
        });
    };

    private onChangeTags = (key: string, values: string[]) => {
        const { filter, onChange } = this.props;
        const { [key]: ignore, ...tags } = toLegacyTagsets(filter.tags) || {};
        onChange({
            ...filter,
            tags: values.length ? { ...tags, [key]: values } : tags,
        });
    };

    private onChangeSeverities = (severities: Severity[]) => {
        const { filter, onChange } = this.props;
        onChange({
            ...filter,
            severities: emptyArrayToUndefined(severities),
        });
    };
}
